/**
 * i18n Configuration
 *
 * Bootstraps i18next only. Language metadata, switching, RTL handling and
 * persistence live in `contexts/LanguageContext.tsx`; detection and the
 * storage key live in `utils/languagePreference.ts` so every consumer (UI,
 * axios clients, demo) agrees on the same language.
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import Backend from 'i18next-http-backend';
import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGE_CODES, detectLanguage, isRTLLanguage } from './utils/languagePreference';

const initialLanguage = detectLanguage();

// Set direction/lang before the first paint so RTL users never see a flip.
document.documentElement.lang = initialLanguage;
document.documentElement.dir = isRTLLanguage(initialLanguage) ? 'rtl' : 'ltr';

i18n
  // Load translations from /locales/{{lng}}/translation.json
  .use(Backend)
  .use(initReactI18next)
  .init({
    lng: initialLanguage,
    fallbackLng: DEFAULT_LANGUAGE,
    supportedLngs: [...SUPPORTED_LANGUAGE_CODES],
    // `pt-BR` etc. resolve to the base language files we ship.
    nonExplicitSupportedLngs: true,
    load: 'languageOnly',

    debug: import.meta.env.DEV,

    interpolation: {
      escapeValue: false, // React already escapes values
    },

    backend: {
      loadPath: '/locales/{{lng}}/translation.json',
    },

    react: {
      useSuspense: true,
    },

    ns: ['translation'],
    defaultNS: 'translation',
    keySeparator: '.',
    returnEmptyString: false,

    // In development, report keys that exist in no loaded language (rendered
    // from an inline default or as the raw key). Keys missing only in the
    // active language silently fall back to English, so those are reported by
    // the bundle comparison below. Both gaps fail CI via src/i18n.locales.test.ts.
    saveMissing: import.meta.env.DEV,
    missingKeyHandler: (lngs, ns, key) => {
      if (import.meta.env.DEV) {
        console.warn(`[i18n] Missing translation "${ns}:${key}" for ${lngs.join(', ')}`);
      }
    },
  });

if (import.meta.env.DEV) {
  const flatKeys = (obj: Record<string, unknown>, prefix = ''): string[] =>
    Object.entries(obj).flatMap(([k, v]) =>
      v && typeof v === 'object' ? flatKeys(v as Record<string, unknown>, `${prefix}${k}.`) : [`${prefix}${k}`]
    );
  const reported = new Set<string>();
  const reportFallbacks = () => {
    const lng = i18n.resolvedLanguage;
    if (!lng || lng === DEFAULT_LANGUAGE || reported.has(lng)) return;
    const active = i18n.getResourceBundle(lng, 'translation');
    const reference = i18n.getResourceBundle(DEFAULT_LANGUAGE, 'translation');
    if (!active || !reference) return;
    reported.add(lng);
    const have = new Set(flatKeys(active));
    const missing = flatKeys(reference).filter(k => !have.has(k));
    if (missing.length) {
      console.warn(`[i18n] ${missing.length} key(s) fall back to English in "${lng}":`, missing);
    }
  };
  i18n.on('loaded', reportFallbacks);
  i18n.on('languageChanged', reportFallbacks);
}

export default i18n;
